/* @flow */
import cx from 'classnames';
import * as React from 'react';
import PropTypes from 'prop-types';

import DeliveryModalContainer from 'chat/components/DeliveryModalContainer.jsx';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import Link from 'components/Link.jsx';
import MessageItemContent from './MessageItemContent.jsx';
import { mountModal } from 'lib/modal.js';
import Text from 'components/Text.jsx';
import Time from 'components/Time.jsx';
import ReactionDisplay from 'chat/components/ReactionDisplay.jsx';
import ReactionStatus from 'chat/components/ReactionStatus.jsx';
import MessageSenderAvatarContainer from 'chat/components/MessageSenderAvatarContainer.jsx';
import { canViewDeliverySummary } from 'chat/lib/DeliverySummaries';
import { ReactionContainer } from 'chat/components/Reaction';

import './styles/AnnouncementMessageItem.scss';

import ChatMessage from 'entities/ChatMessage';
import Message from 'entities/Message';

type Props = {
  message: ChatMessage | Message,
  group: Group,
  subgroupRole?: ?string,
  sentBySelf: boolean,
  senderInitials: string,
  lastOfGroup: boolean,
  disableReactionToggle?: boolean,
};

type State = {
  reactionPickerVisible: boolean,
  hovered: boolean,
};

function getStreamAndMessagePair(message: *) {
  try {
    if (message instanceof ChatMessage) {
      return {
        streamUuid: message.getChatId(),
        messageUuid: message.getUuid(),
      };
    }

    if (message instanceof Message) {
      return {
        streamUuid: message.getGroupThreads()[0].uuid,
        messageUuid: message.getUuid(),
      };
    }
  } catch (e) {
    console.error(e);
  }

  return {
    streamUuid: null,
    messageUuid: null,
  };
}

export class AnnouncementMessageItem extends React.PureComponent<Props, State> {
  state: State = {
    reactionPickerVisible: false,
    hovered: false,
  };

  static contextTypes = {
    threadUUID: PropTypes.string,
  };

  handleHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  handleHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  handleEnterHover = () => {
    this.setState({ hovered: true });
  };

  handleLeaveHover = () => {
    this.setState({ hovered: false });
  };

  getThreadContext = (): ?string => {
    return this.context.threadUUID;
  };

  renderDeliveryInfo() {
    const { message } = this.props;

    const threadUuid = this.getThreadContext();
    const messageUuid = message.getUuid();

    if (!threadUuid || !messageUuid) {
      return null;
    }

    const mount = () => {
      mountModal(
        <DeliveryModalContainer threadUuid={threadUuid} messageUuid={messageUuid} />,
        this
      );
    };

    return (
      <div className="AnnouncementMessageItem-head-deliveryInfo">
        <Link onClick={mount}>See message summary</Link>
      </div>
    );
  }

  renderAnnouncementHeader() {
    const { group, message } = this.props;

    return (
      <div className="AnnouncementMessageItem-head">
        <GroupAvatar className="AnnouncementMessageItem-announcementBadge" group={group} />
        <div className="AnnouncementMessageItem-head-classInfo">
          <Text className="AnnouncementMessageItem-groupName" weight="semibold" truncate>
            {group.getName()}
          </Text>
          {canViewDeliverySummary(message) && this.renderDeliveryInfo()}
        </div>
      </div>
    );
  }

  renderReactionStatus() {
    const { message, sentBySelf } = this.props;
    const { streamUuid, messageUuid } = getStreamAndMessagePair(message);

    if (!streamUuid || !messageUuid) {
      console.error('annoucement message item: could not render reactions status');
      return null;
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionStatus
            message={message}
            sentBySelf={sentBySelf}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderReactions() {
    const { message, sentBySelf, disableReactionToggle } = this.props;
    const { streamUuid, messageUuid } = getStreamAndMessagePair(message);

    if (!streamUuid || !messageUuid) {
      console.error('annoucement message item: could not render reactions');
      return null;
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionDisplay
            sentBySelf={sentBySelf}
            reactionSummary={reactionSummary}
            disableReactionToggle={!!disableReactionToggle}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderHover() {
    const { message } = this.props;

    return (
      <div className="AnnouncementMessageItem-hover">
        <div className="MessageItem-hover-receipt">
          <Time date={message.getSentAt()} hour="numeric" minute="2-digit" />
        </div>
      </div>
    );
  }

  renderDroplet() {
    const { lastOfGroup, message, sentBySelf } = this.props;
    const sender = message.getSender();

    if (!sender) {
      console.error('AnnouncementMessageItem renderDroplet must have a message sender');
      return null;
    }

    const senderUuid = sender.getUuid();
    return lastOfGroup && !sentBySelf ? (
      <span className="AnnouncementMessageItem-droplet">
        <MessageSenderAvatarContainer uuid={senderUuid} />
      </span>
    ) : null;
  }

  render() {
    const { sentBySelf, message } = this.props;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('AnnouncementMessageItem', {
          'AnnouncementMessageItem-self': sentBySelf,
          'AnnouncementMessageItem-other': !sentBySelf,
          'AnnouncementMessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="AnnouncementMessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          {this.renderHover()}
          <div className="AnnouncementMessageItem-column">
            <div className="AnnouncementMessageItem-dropletChubble">
              <div className="AnnouncementMessageItem-chubble">
                {this.renderAnnouncementHeader()}
                <MessageItemContent
                  className="AnnouncementMessageItem-content"
                  sentBySelf={sentBySelf}
                  message={message}
                />
              </div>
              {this.renderDroplet()}
            </div>
            {message.canReact() && this.renderReactionStatus()}
          </div>
        </div>
        {message.canReact() && this.renderReactions()}
      </div>
    );
  }
}

export default AnnouncementMessageItem;
